/**
 * ServiceSurveyManager Component
 * Manages entity-specific service survey configuration
 *
 * Features:
 * - Zero-config default: Automatically uses ALL global questions if not customized
 * - Can select specific global questions
 * - Can add custom entity-specific questions
 * - Can mix global and custom questions
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Car, Plus, Save, RefreshCw, CheckCircle, 
    Library, Sparkles, Edit3
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import { ThemedButton, ThemedCard, ThemedBadge } from '../themed';
import QuestionCard from './QuestionCard';
import axios from '../../axiosConfig';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';

/**
 * ServiceSurveyManager - Manage entity's service survey
 *
 * Zero-config: If not customized, uses all global questions automatically
 * Customized: Entity can select global questions + add custom questions
 */
const ServiceSurveyManager = ({ entityId, onSave, saving }) => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const { entitySettings } = usePreferences(); // Get entity settings for multi-language support

    // Multi-language settings from entity preferences
    const multiLanguageEnabled = entitySettings?.multi_language_enabled || false;
    const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';

    // State
    const [loading, setLoading] = useState(true);
    const [isCustomized, setIsCustomized] = useState(false);
    const [globalQuestions, setGlobalQuestions] = useState([]);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [customQuestions, setCustomQuestions] = useState([]);
    const [, setSurveyConfig] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [showRevertConfirm, setShowRevertConfirm] = useState(false);

    // Load survey configuration on mount
    useEffect(() => {
        loadSurveyConfiguration();
    }, [entityId]);

    /**
     * Load entity's service survey configuration
     */
    const loadSurveyConfiguration = async () => {
        try {
            setLoading(true);

            // Load global questions and entity configuration in parallel
            const [globalResp, configResp] = await Promise.all([
                axios.get(`/api/surveys/library/${entityId}/service?isGlobal=true`),
                axios.get(`/api/surveys/${entityId}/configuration/service`)
            ]);

            const globals = globalResp.data.data || [];
            const config = configResp.data.data;

            setGlobalQuestions(globals);
            setSurveyConfig(config);

            // Check if survey is customized
            if (config && config.survey_id) {
                // Customized survey exists
                setIsCustomized(true);

                // Separate global and custom questions
                const assigned = config.questions || [];
                const selected = assigned.filter(q => q.source_type === 'global');
                const custom = assigned.filter(q => q.source_type === 'custom');

                setSelectedQuestions(selected);
                setCustomQuestions(custom);
            } else {
                // Zero-config: Using all global questions
                setIsCustomized(false);
                setSelectedQuestions(globals); // Show all global questions in preview
                setCustomQuestions([]);
            }

            setHasChanges(false);
        } catch (error) {
            console.error('Error loading service survey:', error);
            toast.error(t('errorLoadingSurveyConfiguration'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Initialize customization
     * Creates entity survey and copies all global questions by default
     */
    const handleStartCustomization = async () => {
        try {
            setLoading(true);

            // Initialize survey with all global questions
            await axios.post(`/api/surveys/${entityId}/configuration/service/initialize`);

            toast.success(t('customizationStarted'));
            loadSurveyConfiguration(); // Reload
        } catch (error) {
            console.error('Error initializing customization:', error);
            toast.error(error.response?.data?.error || t('errorStartingCustomization'));
            setLoading(false);
        }
    };

    /**
     * Revert to zero-config (delete entity survey)
     */
    const handleRevertToDefault = () => {
        setShowRevertConfirm(true);
    };

    const confirmRevertToDefault = async () => {
        try {
            setShowRevertConfirm(false);
            setLoading(true);

            // Delete entity survey (revert to zero-config)
            await axios.delete(`/api/surveys/${entityId}/configuration/service`);

            toast.success(t('revertedToZeroConfig'));
            loadSurveyConfiguration(); // Reload
        } catch (error) {
            console.error('Error reverting to default:', error);
            toast.error(error.response?.data?.error || t('errorRevertingToDefault'));
            setLoading(false);
        }
    };

    /**
     * Add global question to survey
     */
    const handleAddGlobalQuestion = async (questionId) => {
        try {
            await axios.post(
                `/api/surveys/${entityId}/configuration/service/add-global`,
                { question_id: questionId }
            );

            toast.success(t('questionAdded'));
            loadSurveyConfiguration();
        } catch (error) {
            console.error('Error adding global question:', error);
            toast.error(error.response?.data?.error || t('errorAddingQuestion'));
        }
    };

    /**
     * Add custom question
     */
    const handleAddCustomQuestion = () => {
        const newQuestion = {
            question_type: 'scale',
            question_text: '',
            help_text: '',
            is_required: true,
            display_order: (selectedQuestions.length + customQuestions.length) + 1,
            source_type: 'custom'
        };
        setCustomQuestions([...customQuestions, newQuestion]);
        setHasChanges(true);
    };

    /**
     * Handle question change
     */
    const handleQuestionChange = (index, field, value, isCustom) => {
        if (isCustom) {
            const updated = [...customQuestions];
            updated[index] = { ...updated[index], [field]: value };
            setCustomQuestions(updated);
        } else {
            const updated = [...selectedQuestions];
            updated[index] = { ...updated[index], [field]: value };
            setSelectedQuestions(updated);
        }
        setHasChanges(true);
    };

    /**
     * Delete question
     */
    const handleDeleteQuestion = async (index, isCustom) => {
        const questions = isCustom ? customQuestions : selectedQuestions;
        const question = questions[index];

        if (!isCustom && question.source_type === 'global') {
            // Remove global question from survey
            try {
                await axios.delete(
                    `/api/surveys/${entityId}/configuration/service/questions/${question.id}`
                );
                toast.success(t('questionRemoved'));
                loadSurveyConfiguration();
            } catch (error) {
                console.error('Error removing question:', error);
                toast.error(error.response?.data?.error || t('errorRemovingQuestion'));
            }
        } else {
            // Remove custom question (local or backend)
            if (question.id) {
                try {
                    await axios.delete(
                        `/api/surveys/${entityId}/configuration/service/questions/${question.id}`
                    );
                    toast.success(t('questionDeleted'));
                } catch (error) {
                    console.error('Error deleting question:', error);
                    toast.error(error.response?.data?.error || t('errorDeletingQuestion'));
                    return;
                }
            }

            const updated = customQuestions.filter((_, i) => i !== index);
            setCustomQuestions(updated);
            setHasChanges(!question.id);
        }
    };

    /**
     * Save custom questions
     */
    const handleSaveCustomQuestions = async () => {
        try {
            // Validate custom questions
            for (const question of customQuestions) {
                if (!question.question_text) {
                    toast.error(t('questionTextRequired'));
                    return;
                }
            }

            // Save new custom questions
            for (const question of customQuestions) {
                if (!question.id) {
                    await axios.post(
                        `/api/surveys/${entityId}/configuration/service/questions`,
                        question
                    );
                }
            }

            toast.success(t('questionsSaved'));
            setHasChanges(false);
            loadSurveyConfiguration();
        } catch (error) {
            console.error('Error saving custom questions:', error);
            toast.error(error.response?.data?.error || t('errorSavingQuestions'));
        }
    };

    /**
     * Reorder questions
     */
    const handleReorderQuestions = async (newOrder) => {
        try {
            await axios.put(
                `/api/surveys/${entityId}/configuration/service/reorder`,
                { question_order: newOrder }
            );
            toast.success(t('questionsReordered'));
            loadSurveyConfiguration();
        } catch (error) {
            console.error('Error reordering questions:', error);
            toast.error(error.response?.data?.error || t('errorReorderingQuestions'));
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    // Zero-config default view
    if (!isCustomized) {
        return (
            <div className="space-y-6">
                {/* Zero-config status card */}
                <ThemedCard className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                    <div className="flex items-start gap-4">
                        <div className={cn(
                            "w-12 h-12 rounded-full flex items-center justify-center",
                            "bg-blue-500 text-white"
                        )}>
                            <Sparkles className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                            <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                                {t('zeroConfigurationActive')}
                            </h3>
                            <p className={cn("text-sm mb-4", text.secondary)}>
                                {t('zeroConfigDescription')}
                            </p>
                            <div className="flex items-center gap-3">
                                <ThemedButton
                                    onClick={handleStartCustomization}
                                    variant="primary"
                                >
                                    <Edit3 className="w-4 h-4 mr-2" />
                                    {t('customizeSurvey')}
                                </ThemedButton>
                                <ThemedBadge variant="success">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    {globalQuestions.length} {t('globalQuestions')}
                                </ThemedBadge>
                            </div>
                        </div>
                    </div>
                </ThemedCard>

                {/* Preview of global questions */}
                <ThemedCard className="p-6">
                    <h3 className={cn("text-lg font-semibold mb-4", text.primary)}>
                        {t('activeQuestions')} ({globalQuestions.length})
                    </h3>
                    <div className="space-y-3">
                        {globalQuestions.map((question, index) => (
                            <div
                                key={question.id}
                                className={cn("p-3 rounded-lg", background.tertiary)}
                            >
                                <div className="flex items-center gap-2">
                                    <ThemedBadge variant="info" size="sm">
                                        <Library className="w-3 h-3 mr-1" />
                                        {t('global')}
                                    </ThemedBadge>
                                    <span className={cn("font-medium", text.primary)}>
                                        {index + 1}. {question.question_text}
                                    </span>
                                </div>
                                {question.help_text && (
                                    <p className={cn("text-sm mt-1 ml-6", text.secondary)}>
                                        {question.help_text}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </ThemedCard>
            </div>
        );
    }

    // Customized survey view
    const allQuestions = [...selectedQuestions, ...customQuestions];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className={cn("text-lg font-semibold", text.primary)}>
                        {t('customServiceSurvey')}
                    </h3>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('mixGlobalAndCustomQuestions')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {hasChanges && (
                        <ThemedBadge variant="warning">
                            {t('unsavedChanges')}
                        </ThemedBadge>
                    )}
                    <ThemedButton
                        onClick={handleRevertToDefault}
                        variant="outline"
                        size="sm"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        {t('revertToZeroConfig')}
                    </ThemedButton>
                    <ThemedButton
                        onClick={handleSaveCustomQuestions}
                        disabled={!hasChanges || saving}
                    >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? t('saving') : t('saveChanges')}
                    </ThemedButton>
                </div>
            </div>

            {/* Question stats */}
            <div className="flex items-center gap-3">
                <ThemedBadge variant="info">
                    <Library className="w-3 h-3 mr-1" />
                    {selectedQuestions.length} {t('global')}
                </ThemedBadge>
                <ThemedBadge variant="secondary">
                    {customQuestions.length} {t('custom')}
                </ThemedBadge>
                <ThemedBadge variant="success">
                    {allQuestions.length} {t('total')}
                </ThemedBadge>
            </div>

            {/* Add question buttons */}
            <div className="flex gap-3">
                <ThemedButton onClick={handleAddCustomQuestion} variant="outline">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('addCustomQuestion')}
                </ThemedButton>
                {/* TODO: Add modal to select from global questions */}
            </div>

            {/* Questions list */}
            {allQuestions.length === 0 ? (
                <ThemedCard className="p-8">
                    <div className={cn("text-center", text.secondary)}>
                        <Car className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                            {t('noQuestionsConfigured')}
                        </h3>
                        <p className="mb-4">{t('addQuestionsToGetStarted')}</p>
                        <ThemedButton onClick={handleAddCustomQuestion}>
                            <Plus className="w-4 h-4 mr-2" />
                            {t('addQuestion')}
                        </ThemedButton>
                    </div>
                </ThemedCard>
            ) : (
                <div className="space-y-4">
                    {/* Selected global questions */}
                    {selectedQuestions.map((question, index) => (
                        <QuestionCard
                            key={`global-${question.id}`}
                            question={question}
                            index={index}
                            totalQuestions={allQuestions.length}
                            entityId={entityId}
                            isGlobal={true}
                            canEdit={false} // Can't edit global questions
                            canDelete={true}
                            canReorder={true}
                            multiLanguageEnabled={multiLanguageEnabled}
                            entityDefaultLanguage={entityDefaultLanguage}
                            onChange={(i, field, value) => handleQuestionChange(i, field, value, false)}
                            onDelete={(i) => handleDeleteQuestion(i, false)}
                            onMove={handleReorderQuestions}
                        />
                    ))}

                    {/* Custom questions */}
                    {customQuestions.map((question, index) => (
                        <QuestionCard
                            key={`custom-${question.id || index}`}
                            question={question}
                            index={selectedQuestions.length + index}
                            totalQuestions={allQuestions.length}
                            entityId={entityId}
                            isGlobal={false}
                            canEdit={true}
                            canDelete={true}
                            canReorder={true}
                            multiLanguageEnabled={multiLanguageEnabled}
                            entityDefaultLanguage={entityDefaultLanguage}
                            onChange={(i, field, value) => handleQuestionChange(i, field, value, true)}
                            onDelete={(i) => handleDeleteQuestion(i, true)}
                            onMove={handleReorderQuestions}
                        />
                    ))}
                </div>
            )}

            <ConfirmDialogShell
                isOpen={showRevertConfirm}
                onClose={() => setShowRevertConfirm(false)}
                onConfirm={confirmRevertToDefault}
                title={t('revertToZeroConfig')}
                confirmLabel={t('revertToZeroConfig')}
                confirmVariant="warning"
                confirmIcon={RefreshCw}
                showIcon={true}
                iconVariant="warning"
            >
                <p className={cn("text-sm", text.secondary)}>
                    {t('confirmRevertToZeroConfig')}
                </p>
            </ConfirmDialogShell>
        </div>
    );
};

export default ServiceSurveyManager;
